<template>
  <div>
    <!-- Main Footer -->
    <footer class="bg-gradient-to-b from-secondary to-[#50091d] text-white pt-16 pb-8 relative">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          <!-- Brand -->
          <div class="space-y-6">
            <div class="bg-white/10 backdrop-blur-sm p-4 rounded-xl w-max border border-white/20">
              <span class="text-white font-black text-2xl tracking-widest">EVT</span>
            </div>
            <p class="text-gray-300 leading-relaxed font-light">
              {{ $t('footer.description') }}
            </p>
            <div class="flex space-x-3">
              <!-- Facebook -->
              <a 
                v-if="settings.social_facebook" 
                :href="settings.social_facebook" 
                target="_blank"
                class="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#1877F2] hover:text-white transition-all border border-white/10 hover:border-[#1877F2] group"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              
              <!-- Instagram -->
              <a 
                v-if="settings.social_instagram" 
                :href="settings.social_instagram" 
                target="_blank"
                class="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] hover:text-white transition-all border border-white/10 hover:border-[#833AB4] group"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              
              <!-- TikTok -->
              <a 
                v-if="settings.social_tiktok" 
                :href="settings.social_tiktok" 
                target="_blank"
                class="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all border border-white/10 hover:border-black group"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              
              <!-- WhatsApp -->
              <a 
                v-if="settings.social_whatsapp" 
                :href="`https://wa.me/${settings.social_whatsapp.replace(/[^0-9]/g, '')}`" 
                target="_blank"
                class="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#25D366] hover:text-white transition-all border border-white/10 hover:border-[#25D366] group"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Links/Allies -->
          <div>
            <h3 class="font-bold text-xl mb-6 flex items-center">
              {{ $t('footer.allies') }}
              <span class="ml-3 h-px w-12 bg-primary/50 block"></span>
            </h3>
            <div class="bg-white/5 border border-white/10 p-6 rounded-xl hover:bg-white/10 transition-colors cursor-default">
              <p class="font-bold text-lg tracking-wider text-center text-white/80">IATA CERTIFIED</p>
              <p class="text-xs text-center text-gray-400 mt-2">{{ $t('footer.iata_subtitle') }}</p>
            </div>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="font-bold text-xl mb-6 flex items-center">
              {{ $t('footer.contact_us') }}
              <span class="ml-3 h-px w-12 bg-primary/50 block"></span>
            </h3>
            <ul class="space-y-4">
              <li class="flex items-start space-x-3 group">
                <span class="material-icons text-primary mt-1 group-hover:text-white transition-colors">place</span>
                <span class="text-gray-300 group-hover:text-white transition-colors">
                  <template v-if="settings.contact_address">
                    {{ settings.contact_address }}
                  </template>
                  <template v-else>
                    45 SOUTH MAIN ST.<br>FREEPORT, NY 11520
                  </template>
                </span>
              </li>
              <li class="flex items-center space-x-3 group">
                <span class="material-icons text-primary group-hover:text-white transition-colors">call</span>
                <span class="text-gray-300 group-hover:text-white transition-colors">{{ settings.contact_phone || '516-442-1499' }}</span>
              </li>
              <li v-if="settings.social_whatsapp" class="flex items-center space-x-3 group">
                <svg class="w-5 h-5 text-primary group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                 <a :href="`https://wa.me/${settings.social_whatsapp.replace(/[^0-9]/g, '')}`" target="_blank" class="text-gray-300 group-hover:text-white transition-colors hover:underline">
                   {{ settings.social_whatsapp }} ({{ $t('footer.whatsapp') }})
                 </a>
              </li>
              <li class="flex items-center space-x-3 group">
                <span class="material-icons text-primary group-hover:text-white transition-colors">email</span>
                <span class="text-gray-300 group-hover:text-white transition-colors">{{ settings.contact_email || 'eaglesvisiontravel1@gmail.com' }}</span>
              </li>
            </ul>
          </div>

    <!-- Newsletter -->
          <div>
            <h3 class="font-bold text-xl mb-6 flex items-center">
              {{ $t('footer.newsletter_title') }}
              <span class="ml-3 h-px w-12 bg-primary/50 block"></span>
            </h3>
            <p class="text-sm text-gray-300 mb-4">{{ $t('footer.newsletter_desc') }}</p>
            <form @submit.prevent="handleSubscribe" class="space-y-3">
              <div class="relative">
                 <span class="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">email</span>
                  <input 
                   v-model="newsletterEmail"
                   type="email" 
                   required
                   :placeholder="$t('footer.newsletter_placeholder')" 
                   class="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                   :disabled="subscribing"
                  >
              </div>
              <button 
                type="submit"
                :disabled="subscribing"
                class="w-full bg-accent hover:bg-yellow-500 text-gray-900 font-bold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{{ subscribing ? $t('common.loading') : $t('footer.subscribe') }}</span>
                <span v-if="!subscribing" class="material-icons text-sm">send</span>
                <span v-else class="material-icons text-sm animate-spin">sync</span>
              </button>
            </form>
          </div>

        </div>
        
        <!-- Bottom Bar -->
        <div class="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
          <p>&copy; {{ currentYear }} {{ settings.site_name || 'Eagle´s Vision Travel' }}. {{ $t('footer.all_rights') }}</p>
          <div class="flex space-x-6 mt-4 md:mt-0">
             <a href="#" class="hover:text-white transition-colors">{{ $t('footer.privacy') }}</a>
             <a href="#" class="hover:text-white transition-colors">{{ $t('footer.terms') }}</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'TheFooter',
  data() {
    return {
      settings: {},
      newsletterEmail: '',
      subscribing: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  methods: {
    async handleSubscribe() {
      if (!this.newsletterEmail) return

      this.subscribing = true
      
      try {
        const response = await api.post('/newsletter/subscribe', {
          email: this.newsletterEmail
        })
        
        // Use a more elegant toast
        this.$swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          icon: 'success',
          title: 'Newsletter',
          text: response.data.message || '¡Te has suscrito con éxito!',
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer)
            toast.addEventListener('mouseleave', this.$swal.resumeTimer)
          }
        })
        
        this.newsletterEmail = ''
      } catch (error) {
        console.error('Newsletter error:', error)
        this.$swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 5000,
          icon: 'error',
          title: 'Newsletter',
          text: error.response?.data?.message || 'Hubo un problema al procesar tu solicitud'
        })
      } finally {
        this.subscribing = false
      }
    }
  },
  async mounted() {
    try {
      const response = await api.get('/settings')
      
      const flatSettings = {}
      if (typeof response.data === 'object' && response.data !== null) {
          Object.values(response.data).forEach(group => {
            Object.assign(flatSettings, group)
          })
      }
      
      this.settings = flatSettings
    } catch (error) {
      console.error('Error loading settings:', error)
    }
  }
}
</script>
